import React, { useEffect, useState, useCallback } from "react";
import { X, Loader2, AlertCircle, Eye, Clapperboard } from "lucide-react";
import axiosClient from "../../api/axiosClient";
import useHiddenVerticals, { withoutHiddenSectors } from "../../hooks/useHiddenVerticals";
import { canonicalSimulatorSector } from "./sectorCanonical";
import TemplatePreview from "../chat/TemplatePreview";
import PresentationFlowReplica from "./PresentationFlowReplica";
import {
  FLOW_PREVIEWABLE_SECTORS,
  FLOW_TELEVENTA_ONLY_SECTORS,
  SECTOR_LABELS,
  miniTemplateNameForSector,
  buildFlowPreviewTemplates,
  businessTypeForSector,
  hasTeleventaFlow,
} from "./flowTemplates";

/**
 * FlowPreviewModal
 * Vista previa de solo lectura del "Flow" de ventas: los dos mensajes
 * (head específico por sector + barber_mini2 con miniatura) tal como los
 * recibiría el lead por WhatsApp. Incluye un selector de sector para
 * previsualizar cualquiera.
 *
 * Contenido dinámico: las plantillas se cargan de GET /plantillas (versión viva
 * desde Meta, ya que el comercial es conextcrm-e2f0b3) y la miniatura del mini
 * se genera con el mismo endpoint que usa el envío real.
 */
export default function FlowPreviewModal({
  open,
  onClose,
  leadName = "",
  initialSector = null,
  initialOnline = false,
  initialWithPresentation = false,
  initialSingle = false,
  // Se abre desde dentro de SendFlowModal (z-70): el hijo DEBE tener un z-index mayor
  // que el padre o queda detrás. Default bajo para no romper otros usos; el padre lo sube.
  overlayZIndex = 60,
}) {
  const hiddenVerticals = useHiddenVerticals(); // verticales ocultas a este usuario (1/10/2026)
  const [sector, setSector] = useState(
    FLOW_PREVIEWABLE_SECTORS.includes(initialSector) ? initialSector : "barberia",
  );
  // Variante de mini a previsualizar: normal (barber_mini2) o con presentación.
  const [withPresentation, setWithPresentation] = useState(initialWithPresentation);
  // Modalidad: false = presencial (asesor pasa), true = online/televenta (te llaman).
  // En televenta la head usa el cierre online y la mini el flow de videollamada.
  const [online, setOnline] = useState(initialOnline);
  // Con presentación + online en un sector con ad: true = la plantilla única del botón
  // "Flow televenta"; false = los 2 mensajes que manda "Flow + presentación".
  const [singleMessage, setSingleMessage] = useState(initialSingle);
  // Réplica navegable del flow (al pulsar "Solicitar presentación" en la mini).
  const [flowReplicaOpen, setFlowReplicaOpen] = useState(false);
  const [allTemplates, setAllTemplates] = useState(null);
  const [thumbsBySector, setThumbsBySector] = useState({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingThumb, setLoadingThumb] = useState(false);
  const [error, setError] = useState(null);

  // Al abrir, se coloca en la variante desde la que se pulsó el ojo (sector del lead +
  // los toggles de esa variante). Los toggles siguen siendo libres para explorar el resto.
  useEffect(() => {
    if (open) {
      // canonicalSimulatorSector: los leads de la landing traen la etiqueta
      // ("Estética / Spa") y sin normalizar la preview caía a barbería.
      const canonical = canonicalSimulatorSector(initialSector);
      setSector(
        FLOW_PREVIEWABLE_SECTORS.includes(canonical) ? canonical : "barberia",
      );
      setOnline(initialOnline);
      setWithPresentation(initialWithPresentation);
      setSingleMessage(initialSingle);
      setError(null);
    }
  }, [open, initialSector, initialOnline, initialWithPresentation, initialSingle]);

  // Carga las plantillas una sola vez al abrir.
  useEffect(() => {
    if (!open || allTemplates) return;
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      setError(null);
      try {
        const res = await axiosClient.get("/plantillas");
        const list = res?.data || res?.plantillas || res || [];
        if (!cancelled) setAllTemplates(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled)
          setError(err.response?.data?.message || err.message || "Error cargando plantillas");
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, allTemplates]);

  // Genera (y cachea) la miniatura del sector activo.
  const ensureThumb = useCallback(
    async (sec) => {
      if (!open || thumbsBySector[sec]) return;
      setLoadingThumb(true);
      try {
        const thumbRes = await axiosClient.post("/ventas/generate-thumbnail", {
          nombre: leadName,
          sector: sec,
        });
        if (thumbRes?.success && thumbRes.url) {
          setThumbsBySector((prev) => ({ ...prev, [sec]: thumbRes.url }));
        }
      } catch (_) {
        // El preview del mini cae a placeholder si falla la miniatura.
      } finally {
        setLoadingThumb(false);
      }
    },
    [open, leadName, thumbsBySector],
  );

  useEffect(() => {
    if (open) ensureThumb(sector);
  }, [open, sector, ensureThumb]);

  if (!open) return null;

  const thumbUrl = thumbsBySector[sector];
  // Sectores "solo televenta" (taller): no tienen head+mini, así que los toggles de
  // "Flow normal/con presentación" y "Presencial/Full online" no aplican — se ocultan
  // más abajo — y el único mensaje posible es siempre la plantilla única de televenta.
  const isTeleventaOnlySector = FLOW_TELEVENTA_ONLY_SECTORS.includes(sector);
  // Con presentación + full online, en un sector con ad, hay DOS envíos distintos y los dos
  // son alcanzables desde la ficha, así que los dos tienen que poder previsualizarse:
  //   - botón "Flow televenta"     → 1 mensaje (vídeo + todo el texto + los dos botones)
  //   - botón "Flow + presentación" sobre un lead marcado como televenta → los 2 de siempre
  // En un sector "solo televenta" no hay elección posible: no hay "los 2 de siempre".
  const televentaAvailable = !isTeleventaOnlySector && withPresentation && online && hasTeleventaFlow(sector);
  const isTeleventaFlow = isTeleventaOnlySector ? hasTeleventaFlow(sector) : televentaAvailable && singleMessage;
  const built = allTemplates
    ? buildFlowPreviewTemplates(
        allTemplates,
        sector,
        thumbUrl,
        miniTemplateNameForSector(sector, withPresentation, online),
        online,
        isTeleventaFlow,
      )
    : { head: null, mini: null, single: null };
  // Las plantillas del flow usan parámetros posicionales: {{1}} = nombre del lead
  // y, en la head compartida (conext_head6), {{2}} = tipo de negocio del sector.
  // TemplatePreview sustituye {{n}} desde `preview_params`; los inyectamos para
  // que el cuerpo muestre los valores reales (contactName solo cubre el formato NAMED).
  // El mini (barber_mini2) solo tiene {{1}}; el 2º valor sobra sin efecto.
  const businessType = businessTypeForSector(sector);
  const withParams = (tpl) =>
    tpl ? { ...tpl, preview_params: [leadName, businessType] } : null;
  const head = withParams(built.head);
  const mini = withParams(built.mini);
  const single = withParams(built.single);

  return (
    <>
    <div
      style={{ zIndex: overlayZIndex }}
      className="fixed inset-0 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
              <Eye className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-800">Vista previa del Flow</h3>
              <p className="text-[11px] font-medium text-slate-400">
                Lo que recibirá {leadName?.trim() || "el lead"} por WhatsApp
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-xl transition-all active:scale-90"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Selector de sector */}
        <div className="px-6 py-3 border-b border-slate-50 flex flex-wrap gap-2">
          {withoutHiddenSectors(FLOW_PREVIEWABLE_SECTORS, hiddenVerticals).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSector(s)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all active:scale-95 ${
                sector === s
                  ? "bg-emerald-500 text-white shadow-md shadow-emerald-200/50"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
            >
              {SECTOR_LABELS[s] || s}
            </button>
          ))}
        </div>

        {/* Toggle: mini normal vs. con botón "Solicitar presentación". No aplica a los
            sectores "solo televenta" (taller): no tienen head+mini que alternar. */}
        {!isTeleventaOnlySector && (
          <div className="px-6 py-2.5 border-b border-slate-50 flex items-center gap-2">
            {[
              { key: false, label: "Flow normal" },
              { key: true, label: "Con presentación" },
            ].map((opt) => (
              <button
                key={String(opt.key)}
                type="button"
                onClick={() => setWithPresentation(opt.key)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all active:scale-95 ${
                  withPresentation === opt.key
                    ? "bg-indigo-500 text-white shadow-md shadow-indigo-200/50"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Toggle: modalidad presencial (asesor pasa) vs. online/televenta (te llaman).
            Tampoco aplica a "solo televenta": su único mensaje ya es full online. */}
        {!isTeleventaOnlySector && (
          <div className="px-6 py-2.5 border-b border-slate-50 flex items-center gap-2">
            {[
              { key: false, label: "Presencial" },
              { key: true, label: "Full online (televenta)" },
            ].map((opt) => (
              <button
                key={String(opt.key)}
                type="button"
                onClick={() => setOnline(opt.key)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all active:scale-95 ${
                  online === opt.key
                    ? "bg-sky-500 text-white shadow-md shadow-sky-200/50"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Los dos envíos posibles de esta combinación, cada uno con su botón en la ficha. */}
        {televentaAvailable && (
          <div className="px-6 py-2.5 border-b border-slate-50 flex flex-wrap items-center gap-2 bg-fuchsia-50/40">
            <Clapperboard className="w-3.5 h-3.5 text-fuchsia-500 shrink-0" />
            {[
              { key: true, label: "1 mensaje · botón «Flow televenta»" },
              { key: false, label: "2 mensajes · botón «Flow + presentación»" },
            ].map((opt) => (
              <button
                key={String(opt.key)}
                type="button"
                onClick={() => setSingleMessage(opt.key)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-all active:scale-95 ${
                  singleMessage === opt.key
                    ? "bg-fuchsia-500 text-white shadow-md shadow-fuchsia-200/50"
                    : "bg-white text-slate-500 hover:bg-slate-100 border border-fuchsia-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
            <p className="w-full text-[11px] font-medium text-fuchsia-700/80">
              {singleMessage
                ? "Un solo mensaje: el vídeo, la presentación y los dos botones. Sin miniatura."
                : "Los dos mensajes de siempre: la presentación en texto y, aparte, la miniatura con los botones."}
            </p>
          </div>
        )}

        {/* Cuerpo */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-slate-50/50">
          {loadingTemplates && (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-400">
              <Loader2 className="w-7 h-7 animate-spin" />
              <span className="text-xs font-bold uppercase tracking-widest">
                Cargando plantillas…
              </span>
            </div>
          )}

          {error && !loadingTemplates && (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-rose-500">
              <AlertCircle className="w-7 h-7" />
              <span className="text-xs font-bold text-center max-w-xs">{error}</span>
            </div>
          )}

          {!loadingTemplates && !error && allTemplates && (
            <>
              {/* Flow televenta: un único mensaje (sin miniatura que generar). */}
              {isTeleventaFlow ? (
                single ? (
                  <TemplatePreview
                    tpl={single}
                    contactName={leadName}
                    onButtonClick={() => setFlowReplicaOpen(true)}
                  />
                ) : (
                  <MissingTpl label="Plantilla de televenta no disponible" />
                )
              ) : (
                <>
                  {loadingThumb && !thumbUrl && (
                    <div className="flex items-center justify-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Generando miniatura…
                    </div>
                  )}
                  {head ? (
                    <TemplatePreview tpl={head} contactName={leadName} />
                  ) : (
                    <MissingTpl label="Plantilla de cabecera no disponible" />
                  )}
                  {mini ? (
                    <TemplatePreview
                      tpl={mini}
                      contactName={leadName}
                      onButtonClick={withPresentation ? () => setFlowReplicaOpen(true) : null}
                    />
                  ) : (
                    <MissingTpl label="Plantilla de miniatura no disponible" />
                  )}
                </>
              )}
              {withPresentation && (
                <p className="text-center text-[11px] font-medium text-slate-400">
                  Pulsa <span className="font-bold text-emerald-600">"Solicitar presentación"</span> para ver el formulario tal cual lo verá el lead.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
    <PresentationFlowReplica
      open={flowReplicaOpen}
      onClose={() => setFlowReplicaOpen(false)}
      online={online}
      overlayZIndex={overlayZIndex + 10}
    />
    </>
  );
}

function MissingTpl({ label }) {
  return (
    <div className="rounded-[1.5rem] border border-dashed border-slate-200 bg-white/60 py-8 flex flex-col items-center gap-2 text-slate-400">
      <AlertCircle className="w-6 h-6" />
      <span className="text-xs font-bold text-center max-w-xs">{label}</span>
    </div>
  );
}
